/**
 * SA-947 (spec P9, P10, P12): every word the Mailboxes page says about a
 * WhatsApp group source, and the small rules behind them. Pure.
 */
import { ApiError } from '@/services/apiClient'
import type { Mailbox, WahaSourceRead } from '@/services/mailboxes'

export function isGroupSource(mb: Pick<Mailbox, 'channel'>): boolean {
  return mb.channel === 'whatsapp-group'
}

/** SA-943: connect copies the deployment's set into one named
 *  `waha:mailbox:<id8>:…` (`waha_source.connect`); the create form never
 *  offers those. */
export const ROW_OWNED_SET_PREFIX = 'waha:mailbox:'

export function isRowOwnedSet(name: string): boolean {
  return name.startsWith(ROW_OWNED_SET_PREFIX)
}

export function participantsText(count: number): string {
  return `${count} ${count === 1 ? 'participant' : 'participants'}`
}

const LINKED = new Set<Mailbox['status']>(['connecting', 'active', 'paused'])

/** The row's line in place of the email address (spec P10). */
export function groupLine(mb: Pick<Mailbox, 'status' | 'connected_account' | 'group'>): string {
  if (mb.group) {
    return `${mb.group.subject?.trim() || 'Bound group'} · ${participantsText(mb.group.participant_count)}`
  }
  if (mb.connected_account && LINKED.has(mb.status)) return 'Linked · no group chosen yet'
  return 'Not linked'
}

export function boundLine(read: Pick<WahaSourceRead, 'group_subject' | 'participant_count'>): string {
  return `Listening to ${read.group_subject?.trim() || 'the bound group'} · ${participantsText(read.participant_count ?? 0)}`
}

/** WhatsApp shows a pairing code as two groups of four. */
export function pairingCodeText(code: string): string {
  const plain = code.replace(/[^0-9a-z]/gi, '').toUpperCase()
  return plain.length === 8 ? `${plain.slice(0, 4)}-${plain.slice(4)}` : plain
}

export function deleteConfirmText(mb: Pick<Mailbox, 'display_name' | 'status' | 'connected'>): string {
  const name = `Delete "${mb.display_name}"?`
  if (!mb.connected) return `${name} This cannot be undone.`
  if (mb.status === 'paused') {
    return `${name} Its session is stopped, so the phone keeps the link until it is removed under Linked devices. This cannot be undone.`
  }
  return `${name} This logs the desk out of WhatsApp and deletes the source. This cannot be undone.`
}

export const DELETE_ANYWAY_CONFIRM =
  'Delete the source and leave its session on WAHA? The desk stays linked until it is removed under Linked devices on the phone.'

/** The pilot's three steps, in WhatsApp's own words for its menus. */
export const LINK_STEPS = [
  "On the desk's phone, open WhatsApp and go to Settings → Linked devices → Link a device.",
  'Tap "Link with phone number instead" and type the code below, or scan the QR code.',
  'Keep the phone online. This dialog moves on by itself once WhatsApp has linked the desk.',
] as const

export const GROUP_ROUTER_SENTENCE =
  'A WhatsApp group is always a router: it decides what each message is before anything acts on it. Create the source, then add its kinds with Edit routing.'

/** Under the WAHA connection select (spec P9); `count` is null while loading. */
export function wahaSetHint(error: unknown, count: number | null): string {
  if (error instanceof ApiError && error.status === 403) {
    return 'You cannot read credential sets, so no WAHA connection can be listed. Ask an admin.'
  }
  if (error) return 'Failed to load credential sets — reload to choose a WAHA connection.'
  if (count === 0) return 'No credential set holds a WAHA entry yet. Add one under Credential sets.'
  return "The deployment's WAHA base URL and API key. Connect copies them into the source."
}
